import { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, Receipt } from 'lucide-react';
import { CustomSelect } from '../../components/CustomSelect';
import { BillFieldEditor } from '../../components/BillFieldEditor';
import { ReceiptSheet } from '../../components/ReceiptSheet';
import { CHARS_PER_LINE, PAPER_SIZES, receiptColumns } from '../../data/billTemplates';

/**
 * Designing a bill, on a page of its own.
 *
 * It began as a panel beside the template gallery, which meant the field list,
 * the paper settings and the preview all had to fit in half a column — with the
 * gallery still on screen competing for attention. Building a receipt is its
 * own task, not something done out of the corner of an eye.
 *
 * The shape is the one the superadmin app already uses for creating an
 * organization: the tab is replaced by a full page with a back arrow, the work
 * in sections, and one action bar at the end. Two apps, one flow for "make a
 * new thing".
 */
export const BillTemplateEditorPage = ({ org, draft, setDraft, onCancel, onSave, saving, isNew }) => {
  const set = (key, value) => setDraft((d) => ({ ...d, settings: { ...d.settings, [key]: value } }));
  const cols = receiptColumns(draft.settings.paperWidth, draft.settings.charsPerLine);
  const nameGiven = draft.name.trim().length > 0;

  // Open only when the shop has already set an override, so the row stays one
  // line for everyone whose printer matches its paper — which is most of them.
  const [showChars, setShowChars] = useState(draft.settings.charsPerLine != null);
  // The name warning waits for Save. It used to show the moment the page
  // opened, telling a shop off for not having filled in a box they had not
  // reached yet — a page that greets you with a mistake you have not made.
  const [nameMissed, setNameMissed] = useState(false);
  const paperOptions = [];
  let lastGroup = null;
  PAPER_SIZES.forEach((p) => {
    if (p.group !== lastGroup) {
      paperOptions.push({ value: `__${p.group}`, label: p.group, isHeader: true });
      lastGroup = p.group;
    }
    // Size and fit, and nothing else. With the description on the end the
    // label ran to sixty-odd characters in a control about twenty wide, and
    // carried two dashes doing different jobs — so it truncated to
    // "58 mm · 32 characters — Small therma…". The group heading above already
    // says whether it is a roll or a sheet, which is what the note repeated.
    paperOptions.push({
      value: p.mm,
      label: `${p.label} · ${CHARS_PER_LINE[p.mm]} characters`,
    });
  });

  return (
    <article className="flex flex-col gap-4">
      {/* ── Page header ──────────────────────────────────────────── */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-2xl px-5 py-4 flex items-center gap-3">
        <button
          type="button"
          onClick={onCancel}
          aria-label="Go back to templates"
          className="w-8 h-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors shadow-sm shrink-0"
        >
          <ArrowLeft size={16} aria-hidden="true" />
        </button>
        <div
          className="w-10 h-10 rounded-xl text-white flex items-center justify-center shadow-md shrink-0"
          style={{ background: 'var(--theme-primary)' }}
          aria-hidden="true"
        >
          <Receipt size={18} />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-extrabold tracking-tight text-[#0d0d0d] truncate">
            {isNew ? 'New bill design' : 'Edit bill design'}
          </h2>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400 truncate">
            {draft.name || 'Untitled design'}
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_24rem] gap-4 items-start">
        <div className="flex flex-col gap-4 min-w-0">
          {/* ── The bill itself ────────────────────────────────────── */}
          <section className="bg-white border border-[var(--theme-tertiary)] rounded-2xl p-5">
            <h3 className="text-sm font-bold text-[#0d0d0d] mb-1 uppercase tracking-[0.1em]">
              What prints on the bill
            </h3>
            <p className="text-[11px] text-slate-500 mb-4 pb-3 border-b border-slate-100">
              Every line, top to bottom. Drag to reorder, press ⚙ to change one, or add your own.
            </p>
            <BillFieldEditor
              org={org}
              fields={draft.settings.fields || []}
              onChange={(fields) => set('fields', fields)}
            />
          </section>
        </div>

        {/* ── Live preview ─────────────────────────────────────────── */}
        {/* Sticky: the field list runs long, and a preview you have to scroll
            back up to see is a preview nobody looks at while editing. */}
        <aside className="lg:sticky lg:top-4">
          {/* The name and the paper live here, not in a settings section of
              their own. Both describe the thing on show: the paper is the
              sheet being drawn, and the name is what to call it. Kept apart,
              they were two rows of form asking about something pictured a
              column away. */}
          <section className="bg-white border border-[var(--theme-tertiary)] rounded-2xl p-4">
            <div className="flex flex-col gap-3 mb-3">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                  Design name
                </span>
                {/* The hint is styled as one. At the field's own bold weight and
                    near-black colour a placeholder reads as a name already
                    filled in, so a shop could not tell whether "Counter bill"
                    was its design's name or a suggestion. */}
                <input
                  value={draft.name}
                  onChange={(e) => { setNameMissed(false); setDraft((d) => ({ ...d, name: e.target.value })); }}
                  placeholder="e.g. Counter bill"
                  className="w-full bg-transparent border-b border-slate-200 text-[13px] font-bold text-[#0d0d0d] placeholder:font-normal placeholder:italic placeholder:text-slate-300 outline-none focus:border-[var(--theme-primary)] py-1"
                />
                {nameMissed && !nameGiven && (
                  <span className="text-[10px] text-amber-600">
                    Give it a name you will recognise in the branch list.
                  </span>
                )}
              </label>

              {/* A div, not a label. A <label> hands every click inside it to
                  its control, so pressing "Not matching?" — a button sitting
                  within it — also opened the paper dropdown. */}
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                  Paper
                </span>
                <CustomSelect
                  value={draft.settings.paperWidth}
                  onChange={(v) => set('paperWidth', Number(v))}
                  options={paperOptions}
                  className="w-full normal-case text-xs font-semibold text-slate-700"
                />

                {/* Under the paper, not under the bill. The count is a fact
                    about the paper chosen directly above it; sitting at the
                    foot of the preview it read as a note about the picture. */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] text-slate-500">
                    Printing <strong className="text-slate-700">{cols.width}</strong> characters a line
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowChars((v) => !v)}
                    className="text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: 'var(--theme-primary)' }}
                  >
                    {showChars ? 'Use the standard' : 'Not matching?'}
                  </button>
                </div>
                {showChars && (
                  <>
                    <input
                      type="number"
                      min={20}
                      max={200}
                      autoFocus
                      value={draft.settings.charsPerLine ?? ''}
                      onChange={(e) => set('charsPerLine', e.target.value === '' ? null : Number(e.target.value))}
                      placeholder={`${CHARS_PER_LINE[draft.settings.paperWidth] || 48}`}
                      className="w-28 bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] rounded-md px-3 py-2 text-xs font-medium outline-none focus:border-[var(--theme-primary)] focus:bg-white transition-colors"
                    />
                    <p className="text-[10px] text-slate-400 leading-relaxed">
                      Print one bill, count the characters in the longest line, and put that
                      number here. Clear it to go back to the standard.
                    </p>
                  </>
                )}
              </div>
            </div>
            {/* The cap is the screen, not a guessed fraction of it. At a fixed
                62vh a bill well short of the box still got a scrollbar on a
                laptop, because 62vh is smaller than the bill needs on some
                screens and larger on others. Tied to the viewport minus what
                sits above it, the bar appears only when the bill truly cannot
                fit — which is the only time it means anything. */}
            <div className="max-h-[calc(100vh-15rem)] overflow-y-auto custom-scrollbar">
              <ReceiptSheet org={org} settings={draft.settings} caption={false} />
            </div>


          </section>
        </aside>
      </div>

      {/* ── Action bar ───────────────────────────────────────────── */}
      {/* Two buttons on both pages: leave, or keep. A pair — same height, same
          radius, same type — with Cancel outlined rather than filled, because
          only one of the two is the action the page is for.

          Deleting is not here. It belongs on the design's own card in the
          gallery, beside the thing it destroys, not next to Save on the page
          where the work is being done. */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-2xl px-5 py-4 flex items-center justify-end gap-3 flex-wrap">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="h-10 px-6 rounded-lg border border-[var(--theme-tertiary)] bg-white text-xs font-semibold uppercase tracking-[0.14em] text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => (nameGiven ? onSave() : setNameMissed(true))}
          disabled={saving}
          className="h-10 px-6 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'var(--theme-primary)' }}
        >
          {saving ? 'Saving…' : isNew ? 'Create design' : 'Save changes'}
        </button>
      </section>

    </article>
  );
};

BillTemplateEditorPage.propTypes = {
  org: PropTypes.object.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  isNew: PropTypes.bool,
};
